import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

// material import
import {
  Box,
  CircularProgress,
  Grid2,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

// project import
import DashboardHeader from "../../ui-components/dashboardheader";
import LogoutIcon from "@mui/icons-material/Logout";
import PopUpModal from "../../ui-components/PopUpModal";
import LogOutConfirmation from "../userDashboard/logOutConfirmation";
import ComingWeeksDonations from "./comingWeeksDonations";
import PackageDetails from "./packageDetails";
import AmountsDetails from "./amountsDetails";
import TodayDonation from "./todayDonation";
import InActiveDonors from "./inActiveDonors";
import SystemHealth from "./systemHealth";

// service import
import AdminDBServices from "../../services/v1/adminDBServices";
import { formatDateForDisplay } from "../../utils/dateUtils";

const Index = () => {
  const location = useLocation();
  const { name } = location.state || {};
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [isLogoutOpen, setIsLogoutOpen] = useState(false);
  const [donations, setDonations] = useState([]);
  const [inActiveDonors, setInActiveDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Fetch all coming weeks donations and map data
  const fetchAllDonations = async () => {
    setLoading(true);
    try {
      const response = await AdminDBServices.getAllDonations();
      if (response && response.data) {
        
        // 1. Flatten the array so every recurring date gets its own row
        const flattenedDonations = response.data.data.flatMap((donation) => {
          return donation.recurring_dates.map((rd) => ({
            reservationID: rd.reservation_id || "-",
            donorID: donation.donor_details?.donorID,
            donorName: donation.donor_details?.fullname || "-",
            title: donation.donor_details?.prof_title || "-",
            contactNo: donation.donor_details?.contactNo || "-",
            whatsappNo: donation.donor_details?.whatsappNo || "-",
            email: donation.donor_details?.email || "-",
            city: donation.donor_details?.city || "-",
            country: donation.donor_details?.country || "-",
            
            // SPECIFIC date and status for the Table Row
            reservedDate: rd.date ? formatDateForDisplay(rd.date, 'YYYY-MM-DD') : "-",
            monthlyPaymentStatus: rd.payment_status === 1 ? "Success" : "Pending",
            
            package: donation.reservation_details?.package_title || "-",
            packageAmount: donation.reservation_details?.package_price || "-",
            frequency: donation.donor_details?.frequency_name || "-",
            type: donation.donor_details?.type_name || "-",
            message: donation.donor_details?.message || "-",
            reservationNumber: donation.donor_details?.reservation_number || "-",
            paymentMethodID: donation.donor_details?.paymentMethodID || "-",
            paymentMethod: donation.donor_details?.payment_type || "-",
            paymentOption: donation.donor_details?.payment_option || "-",
            packageID: donation.reservation_details?.packageID || "-",
            frequencyID: donation.donor_details?.frequencyID || "-",
            typeID: donation.donor_details?.typeID || "-",

            // ---> ADD THESE BACK FOR THE MODAL (DonorDetails.js) <---
            reservedDates: donation.recurring_dates.map((r) => r.date || "-"),
            paymentStatus: donation.recurring_dates.map((r) =>
              r.payment_status === 1 ? "Paid" : "Pending"
            ),
            recurring_dates: donation.recurring_dates // Passing the raw array just in case
          }));
        });

        // 2. Add sequential numbers to the flattened rows
        const mappedDonations = flattenedDonations.map((item, index) => ({
          ...item,
          no: `#${index + 1}`
        }));

        setDonations(mappedDonations);
        console.log("Mapped Donations:", mappedDonations);
      }
    } catch (error) {
      console.error("Error fetching donations:", error);
      setError("Failed to fetch donations.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all Inactive donors and reservation data
  const fetchAllInactiveDonors = async () => {
    setLoading(true);
    try {
      const response = await AdminDBServices.getAllInactiveDonors();
      if (response && response.data) {
        
        const donations = response.data.data.map((donation, index) => ({
          no: `#${index + 1}`,
          reservationID: donation.reservationID,
          donorID: donation.donorID,
          donorName: donation.donor_details.fullname,
          title: donation.donor_details.prof_title,
          contactNo: donation.donor_details.contactNo,
          whatsappNo: donation.donor_details.whatsappNo || "-",
          email: donation.donor_details.email,
          city: donation.donor_details.city || "-",
          country: donation.donor_details.country,
          reservedDate: formatDateForDisplay(donation.reservation_details.date, 'YYYY-MM-DD'),
          paymentType:
            donation.reservation_details.payment_type === "Pay now"
              ? "Success"
              : "Pending",
          package: donation.reservation_details.package_title,
          packageAmount: donation.reservation_details.package_price,
          frequency: donation.reservation_details.frequency_name || "-",
          type: donation.reservation_details.recurring_type,
          message: donation.reservation_details.message,
          reservationNumber: donation.reservation_details.reservation_number,
          paymentMethodID: donation.reservation_details.paymentMethodID,
          paymentMethod: donation.reservation_details.payment_type,
          paymentOption: donation.reservation_details.payment_option,
          packageID: donation.reservation_details.packageID,
          frequencyID: donation.reservation_details.frequencyID,
          typeID: donation.reservation_details.typeID,
        }));
        setInActiveDonors(donations);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching donations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDonations();
    fetchAllInactiveDonors();
    
  }, []);

  return (
    <Box>
      <DashboardHeader name={name} handleClick={handleClick} open={open} />
      <Grid2 container justifyContent="center">
        <Grid2 item size={{ xs: 8.2 }}>
          <Typography variant="h5" sx={{ fontWeight: "medium", mb: 5, mt: 7 }}>
            Today's Donation
          </Typography>
          <Grid2 container spacing={2}>
            <Grid2 item size={{ xs: 9 }}>
              <Box>
                <TodayDonation />
              </Box>
            </Grid2>
            <Grid2 item size={{ xs: 3 }}>
              <AmountsDetails />
            </Grid2>
          </Grid2>
          <Typography variant="h5" sx={{ fontWeight: "medium", mb: 5, mt: 7 }}>
            Donation Details
          </Typography>
          <Box>
            {loading ? (
              <Box align="center">
                <CircularProgress color="secondary" />
              </Box>
            ) : error ? (
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            ) : (
              <ComingWeeksDonations
                donations={donations}
                fetchAllDonations={fetchAllDonations}
                fetchAllInactiveDonors={fetchAllInactiveDonors}
              />
            )}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: "medium", mb: 5, mt: 7 }}>
            Inactive Donors
          </Typography>
          <Box>
            {loading ? (
              <Box align="center">
                <CircularProgress color="secondary" />
              </Box>
            ) : error ? (
              <Typography variant="body1" color="error">
                {error}
              </Typography>
            ) : (
              <InActiveDonors
                inActiveDonors={inActiveDonors}
                fetchAllDonations={fetchAllDonations}
                fetchAllInactiveDonors={fetchAllInactiveDonors}
              />
            )}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: "medium", mb: 5, mt: 7 }}>
            Packages
          </Typography>
          <Grid2 container spacing={3} sx={{ justifyContent: "center", mt: 3 }}>
            <PackageDetails />
          </Grid2>
          <Typography variant="h5" sx={{ fontWeight: "medium", mb: 5, mt: 7 }}>
            System Health
          </Typography>
          <Box sx={{ mb: 7 }}>
            <SystemHealth />
          </Box>
        </Grid2>
      </Grid2>
      <Box>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={() => setIsLogoutOpen(true)}>
            <ListItemIcon>
              <LogoutIcon color="error" fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </Box>

      <Box>
        <PopUpModal
          open={isLogoutOpen}
          handleCloseDialog={() => setIsLogoutOpen(false)}
          content={
            <LogOutConfirmation
              handleCloseDialog={() => setIsLogoutOpen(false)}
            />
          }
          maxWidth={400}
        />
      </Box>
    </Box>
  );
};

export default Index;
